import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { BRAND_ID_PARAM, ROUTE_GIFT_CARDS } from '../../config/routes';

import { REWARD_AMOUNT_TYPES_ID } from '../../config/rewards';
import { TAKE_BACK_BAG_URL } from '../../config/externalURL';
import { getGiftCardBrand, issueGiftCard } from '../../utils/service';
import ArrowBack from '../../images/circle-arrow-left-gray.svg';
import TCCoin from '../../images/trashie-cash-v3.svg';
import LoadingBar from '../../global/LoadingBar';
import GeneralModal from '../../global/GeneralModal';
import StickyButton from '../../global/StickyButton/StickyButton';
import {
  RewardContentError,
  RewardContentLoading,
} from '../Reward/RewardContent';
import { isMobile } from '../../../../hooks/useBreakPoint';
import {
  GiftCardContentConfirm,
  GiftCardContentMain,
  GiftCardContentSuccess,
  GiftCardContentTerms,
} from './GiftCardContent';
import { getGiftCardBrandValues } from '../../utils/giftCards';
import useHasAccessToGiftCards from '../../../../hooks/useHasAccessToGiftCards';
import { GENERIC_CODE_ERROR, NOT_FOUND_ERROR } from '../../utils/errors';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import { pushGiftCardRedeemedEvent } from '../../utils/gtm';

import './GiftCardBrand.scss';

const MAIN_ELEMENT_ID = 'GiftCardBrand';

const VIEW_STATE = {
  LOADING: 'loading',
  AVAILABLE: 'available',
  ERROR: 'error',
  TERMS: 'terms',
  NO_ACCESS: 'no-access',
};

const MODAL_STATE = {
  LOADING: 'loading',
  ERROR: 'error',
  TERMS: 'terms',
  CONFIRM: 'confirm',
  FINAL: 'final',
};

const GiftCardBrand = () => {
  const rewardsV2Enabled = useRewardsV2Flag();
  const { userData, isLoading: isLoadingFirebaseAuth } = useFirebaseAuth();
  const {
    [BRAND_ID_PARAM]: brandId,
  } = useParams();
  const history = useHistory();
  const timeoutRef = useRef(null);
  const mobile = isMobile();
  const {
    isLoading: isLoadingHasAccessToGiftCards,
    hasAccessToGiftCards,
  } = useHasAccessToGiftCards();

  const [brand, setBrand] = useState({});
  const [value, setValue] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [viewState, setViewState] = useState(VIEW_STATE.LOADING);
  const [modalState, setModalState] = useState(MODAL_STATE.LOADING);
  const [giftCard, setGiftCard] = useState({});
  const [errorId, setErrorId] = useState(null);
  const [isPhoneFlowActive, setIsPhoneFlowActive] = useState(false);

  const toggleModal = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  const handleSelect = useCallback(newValue => {
    setValue(newValue);
  }, []);

  const scrollToTop = useCallback(() => {
    const mainElement = document.getElementById(MAIN_ELEMENT_ID).parentElement;

    if (mainElement) {
      timeoutRef.current = setTimeout(() => {
        mainElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, []);

  const {
    minDenomination,
    minBalance,
    disabled,
    ratio,
  } = useMemo(() => (
    getGiftCardBrandValues({
      giftCardBrand: brand,
      currentBalance,
    })
  ), [brand, currentBalance]);

  const submitDisabled = useMemo(() => (
    currentBalance < minBalance || (currentBalance * ratio) < value
  ), [currentBalance, minBalance, value, ratio]);

  const loadGiftCardBrand = useCallback(async () => {
    setViewState(VIEW_STATE.LOADING);

    const {
      data,
      error: giftCardBrandError,
    } = await getGiftCardBrand(brandId);

    if (giftCardBrandError) {
      setBrand({});
      setValue(0);
      setCurrentBalance(0);
      setErrorId(giftCardBrandError.code || NOT_FOUND_ERROR);
      setViewState(VIEW_STATE.ERROR);
      return;
    }

    const {
      balance: {
        currentBalance: currentBalanceData,
      },
    } = userData;

    const {
      minDenomination: initialValue,
    } = getGiftCardBrandValues({
      giftCardBrand: data.brand,
      currentBalance: currentBalanceData,
    });

    setBrand(data.brand);
    setValue(initialValue);
    setCurrentBalance(currentBalanceData);
    setErrorId(null);
    setViewState(VIEW_STATE.AVAILABLE);
    scrollToTop();
  }, [brandId, userData]);

  const handleOpenConfirm = useCallback(() => {
    setModalState(MODAL_STATE.CONFIRM);
    toggleModal();
  }, [toggleModal]);

  const handleSubmit = useCallback(async () => {
    setModalState(MODAL_STATE.LOADING);

    const {
      data,
      error: claimGiftCardError,
    } = await issueGiftCard({
      brand: brand.slug,
      amount: value,
    });

    if (claimGiftCardError) {
      setGiftCard({});
      setErrorId(claimGiftCardError.code || GENERIC_CODE_ERROR);
      setModalState(MODAL_STATE.ERROR);
      return;
    }

    setGiftCard({
      uid: data.savedGiftCardOrderId,
      amount: value,
    });
    setErrorId(null);
    pushGiftCardRedeemedEvent({ giftCardName: brand.name, giftCardValue: value, userData });
    setModalState(MODAL_STATE.FINAL);
  }, [brand, value, userData]);

  const getGiftCardContent = useCallback(state => {
    switch (state) {
      case VIEW_STATE.AVAILABLE:
        return (
          <GiftCardContentMain
            giftCardBrandItem={brand}
            selectedValue={value}
            onSelect={handleSelect}
            onSubmit={handleOpenConfirm}
            onShowTerms={() => {
              setModalState(MODAL_STATE.TERMS);
              toggleModal();
            }}
            currentBalance={currentBalance}
            disabled={disabled}
            submitDisabled={submitDisabled}
            minBalance={minBalance}
            minDenomination={minDenomination}
            hasActiveSubscription={userData?.activeSubscription}
          />
        );
      case VIEW_STATE.ERROR:
        return (
          <RewardContentError
            errorId={errorId}
            isGiftCard
          />
        );
      case VIEW_STATE.NO_ACCESS:
        return (
          <div className="GiftCardBrand__content--section-no-access">
            You do not have access to gift cards
          </div>
        );
      case VIEW_STATE.LOADING:
      default:
        return <LoadingBar />;
    }
  }, [
    brand,
    currentBalance,
    value,
    disabled,
    submitDisabled,
    errorId,
    handleSelect,
    handleSubmit,
    toggleModal,
  ]);

  const getModalContent = useCallback(() => {
    switch (modalState) {
      case MODAL_STATE.LOADING:
        return (
          <RewardContentLoading
            rewardItem={{
              amountType: REWARD_AMOUNT_TYPES_ID.VALUE,
              rewardAmount: value,
              store: {
                title: brand.name,
              },
            }}
          />
        );
      case MODAL_STATE.ERROR:
        return (
          <RewardContentError
            onBack={toggleModal}
            errorId={errorId}
            isGiftCard
          />
        );
      case MODAL_STATE.TERMS:
        return (
          <GiftCardContentTerms
            giftCardBrandItem={brand}
            onClose={toggleModal}
          />
        );
      case MODAL_STATE.CONFIRM:
        return (
          <GiftCardContentConfirm
            giftCardBrandItem={brand}
            giftCard={{
              amount: value,
              type: REWARD_AMOUNT_TYPES_ID.VALUE,
            }}
            userData={userData}
            onSubmit={handleSubmit}
            onClose={toggleModal}
            onPhoneFlowActive={setIsPhoneFlowActive}
          />
        );
      case MODAL_STATE.FINAL:
        return (
          <GiftCardContentSuccess
            giftCardBrandItem={brand}
            giftCard={giftCard}
            onFinish={toggleModal}
          />
        );
      default:
        return null;
    }
  }, [modalState, brand, giftCard, value, toggleModal, brand.name, userData]);

  const handleBackToUrl = useCallback(() => {
    history.push(ROUTE_GIFT_CARDS);
  }, [history]);

  const {
    buttonLabel,
    buttonOnClick,
    stickySectionSubtitle,
  } = useMemo(() => {
    if (submitDisabled) {
      return {
        buttonLabel: 'BUY A TAKE BACK BAG',
        buttonOnClick: () => {
          window.location.href = TAKE_BACK_BAG_URL;
        },
        stickySectionSubtitle: (
          <>
            <span>You need more </span>
            <img src={TCCoin} alt="TCH" />
          </>
        ),
      };
    }

    return {
      buttonLabel: 'GET THIS GIFT CARD',
      buttonOnClick: handleOpenConfirm,
      stickySectionSubtitle: `$${value} Gift Card`,
    };
  }, [submitDisabled, viewState, value, brand, history, handleOpenConfirm]);

  const handleNoAccess = useCallback(() => {
    setViewState(VIEW_STATE.NO_ACCESS);
  }, []);

  useEffect(() => {
    if (isLoadingFirebaseAuth || isLoadingHasAccessToGiftCards) {
      return;
    }

    if (hasAccessToGiftCards && userData && brandId) {
      loadGiftCardBrand();
    } else {
      handleNoAccess();
    }
  }, [
    isLoadingFirebaseAuth,
    userData,
    brandId,
    isLoadingHasAccessToGiftCards,
    hasAccessToGiftCards,
  ]);

  useEffect(() => (
    () => timeoutRef?.current && clearTimeout(timeoutRef.current)
  ), []);

  return (
    <div id={MAIN_ELEMENT_ID} className={classNames('GiftCardBrand', { 'GiftCardBrand__rewards-v2': rewardsV2Enabled })}>
      <div className="GiftCardBrand__content">
        <div className="GiftCardBrand__content--container">
          {!mobile && (
            <button
              className="GiftCardBrand__content--button"
              type="button"
              onClick={handleBackToUrl}
            >
              <img src={ArrowBack} alt="arrow-back" />
              GO TO ALL GIFT CARDS
            </button>
          )}
          <div className="GiftCardBrand__content--section">
            {viewState === VIEW_STATE.LOADING ? (
              <LoadingBar className="GiftCardBrand__content--loader" />
            ) : (
              getGiftCardContent(viewState)
            )}
          </div>
        </div>
      </div>
      {hasAccessToGiftCards &&
        viewState !== VIEW_STATE.LOADING &&
        viewState !== VIEW_STATE.ERROR
        && (
          <StickyButton
            buttonLabel={buttonLabel}
            onSubmit={buttonOnClick}
            title={viewState !== VIEW_STATE.FINAL && !submitDisabled ? brand.name : null}
            subTitle={stickySectionSubtitle}
            showDescription={viewState !== VIEW_STATE.FINAL}
            typeSecondary={submitDisabled}
          />
        )}
      <GeneralModal
        onClose={toggleModal}
        showModal={showModal}
        showModalHeader={false}
        showModalFooter={false}
        canClose={
          (modalState === MODAL_STATE.TERMS || modalState === MODAL_STATE.CONFIRM)
          && !isPhoneFlowActive
        }
        showClose={
          (modalState === MODAL_STATE.TERMS || modalState === MODAL_STATE.CONFIRM)
          && !isPhoneFlowActive
        }
      >
        {getModalContent()}
      </GeneralModal>
    </div>
  );
};

export default GiftCardBrand;
